import cx from "classnames";
import type { PropsWithChildren } from "react";
import { canv } from "@/canv/global";
import i18n, { normalizeLanguage, t } from "@/i18n";
import styles from "./settingsWindow.module.css";

type SettingsWindowLayoutProps = {
    onClick: (codeName: string) => void;
};

export const SettingsWindowLayout = ({ onClick }: SettingsWindowLayoutProps) => {
    return (
        <div className={styles.settingsWindow}>
            {!canv.isGrantedAccess() && canv.isBoardModeEditOrOwner() && (
                <SettingsWindowItemLayout onClick={onClick} codeName="backToFiles" needDivider />
            )}
            {!canv.isBoardModeView() && (
                <SettingsWindowItemLayout codeName="edit">
                    <SettingsWindowItemLayout onClick={onClick} codeName="undo" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="redo" needDivider />
                    <SettingsWindowItemLayout onClick={onClick} codeName="copy" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="paste" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="selectAll" />
                </SettingsWindowItemLayout>
            )}
            <SettingsWindowItemLayout codeName="view">
                <SettingsWindowItemLayout onClick={onClick} codeName="zoomIn" />
                <SettingsWindowItemLayout onClick={onClick} codeName="zoomOut" />
                <SettingsWindowItemLayout onClick={onClick} codeName="zoomToSelection" />
            </SettingsWindowItemLayout>
            {!canv.isBoardModeView() && (
                <SettingsWindowItemLayout codeName="arrange" needDivider>
                    <SettingsWindowItemLayout onClick={onClick} codeName="alignLeft" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="alignRight" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="alignHCenters" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="alignTop" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="alignVCenters" />
                    <SettingsWindowItemLayout onClick={onClick} codeName="alignBottom" />
                </SettingsWindowItemLayout>
            )}
            <SettingsWindowItemLayout codeName="navigation">
                <SettingsWindowItemLayout onClick={onClick} codeName="navTrackpad" />
                <SettingsWindowItemLayout onClick={onClick} codeName="navMouse" />
            </SettingsWindowItemLayout>
            <SettingsWindowItemLayout codeName="language">
                <SettingsWindowItemLayout onClick={onClick} codeName="setLanguageRu" />
                <SettingsWindowItemLayout onClick={onClick} codeName="setLanguageEn" />
            </SettingsWindowItemLayout>
            {!canv.isBoardModeView() && (
                <SettingsWindowItemLayout onClick={onClick} codeName="shortcuts" />
            )}
            {!canv.isGrantedAccess() && canv.isBoardModeEditOrOwner() && (
                <SettingsWindowItemLayout onClick={onClick} codeName="share" />
            )}
            {!canv.isBoardModeView() && (
                <SettingsWindowItemLayout onClick={onClick} codeName="placeImage" />
            )}
        </div>
    );
};

type SettingsWindowItemLayoutProps = {
    codeName: string;
    needDivider?: boolean;
    onClick?: (codeName: string) => void;
};

export const SettingsWindowItemLayout = ({
    codeName,
    needDivider,
    children,
    onClick,
}: PropsWithChildren<SettingsWindowItemLayoutProps>) => {
    const { name, shortcut } = getNameAndShortcutByCodeName(codeName);
    const decodedShortcut = new DOMParser().parseFromString(shortcut, "text/html").documentElement
        .textContent;

    const isToggle = ["navTrackpad", "navMouse", "setLanguageRu", "setLanguageEn"].includes(
        codeName,
    );

    if (isToggle) {
        const currentLanguage = normalizeLanguage(i18n.language);

        let desription = "";

        if (codeName === "navTrackpad") {
            desription = t("settings.trackpadDescription");
        } else if (codeName === "navMouse") {
            desription = t("settings.mouseDescription");
        }

        const className = cx(styles.settingsWindowItem, styles.settingsWindowItemToggle, {
            [styles.settingsWindowItemNavChoosen]:
                (codeName === "navTrackpad" && canv.actionManager.mode === "trackpad") ||
                (codeName === "navMouse" && canv.actionManager.mode === "mouse") ||
                (codeName === "setLanguageRu" && currentLanguage === "ru") ||
                (codeName === "setLanguageEn" && currentLanguage === "en"),
        });

        return (
            <div className={className} onClick={() => onClick?.(codeName)}>
                <div className={styles.settingsWindowItemNavCircle}></div>
                <div className={styles.settingsWindowItemNav}>
                    <p className={styles.settingsWindowItemName}>{name}</p>
                    {desription && (
                        <p
                            className={styles.settingsWindowItemNavDescr}
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: There will always be static text here.
                            dangerouslySetInnerHTML={{ __html: desription }}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={styles.settingsWindowItem} onClick={() => onClick?.(codeName)}>
                <p className={styles.settingsWindowItemName}>{name}</p>
                {shortcut && (
                    <>
                        <p className={styles.settingsWindowItemShortcut}>{decodedShortcut}</p>
                        {children && (
                            <div className={styles.settingsWindowSecond}>
                                <div className={styles.childrenContainer}>{children}</div>
                            </div>
                        )}
                    </>
                )}
                {codeName === "placeImage" && (
                    <input type="file" className={styles.settingsWindowItemImage} multiple />
                )}
            </div>
            {needDivider && <div className={styles.settingsWindowDividor}></div>}
        </>
    );
};

function getNameAndShortcutByCodeName(codeName: string) {
    const actionSymbol = canv.actionManager.actionSymbol;

    if (codeName === "backToFiles") {
        return { name: t("settings.backToBoards"), shortcut: "" };
    } else if (codeName === "share") {
        return { name: t("settings.share"), shortcut: "" };
    } else if (codeName === "navigation") {
        return { name: t("settings.navigation"), shortcut: "&#62" };
    } else if (codeName === "language") {
        return { name: t("settings.language"), shortcut: "&#62" };
    } else if (codeName === "shortcuts") {
        return { name: t("settings.shortcuts"), shortcut: "" };
    } else if (codeName === "edit") {
        return { name: t("settings.edit"), shortcut: "&#62" };
    } else if (codeName === "view") {
        return { name: t("settings.view"), shortcut: "&#62" };
    } else if (codeName === "placeImage") {
        return { name: t("settings.upload"), shortcut: "" };
    } else if (codeName === "undo") {
        return { name: t("settings.undo"), shortcut: `${actionSymbol} Z` };
    } else if (codeName === "redo") {
        return { name: t("settings.redo"), shortcut: `${actionSymbol} Shift Z` };
    } else if (codeName === "cut") {
        return { name: t("settings.cut"), shortcut: `${actionSymbol} X` };
    } else if (codeName === "copy") {
        return { name: t("settings.copy"), shortcut: `${actionSymbol} C` };
    } else if (codeName === "paste") {
        return { name: t("settings.paste"), shortcut: `${actionSymbol} V` };
    } else if (codeName === "selectAll") {
        return { name: t("settings.selectAll"), shortcut: `${actionSymbol} A` };
    } else if (codeName === "zoomIn") {
        return { name: t("settings.zoomIn"), shortcut: "" };
    } else if (codeName === "zoomOut") {
        return { name: t("settings.zoomOut"), shortcut: "" };
    } else if (codeName === "zoomTo100") {
        return { name: t("settings.zoomReset"), shortcut: `${actionSymbol} 0` };
    } else if (codeName === "zoomToSelection") {
        return { name: t("settings.zoomToSelection"), shortcut: "" };
    } else if (codeName === "arrange") {
        return { name: t("settings.arrange"), shortcut: "&#62" };
    } else if (codeName === "alignLeft") {
        return { name: t("settings.alignLeft"), shortcut: "" };
    } else if (codeName === "alignHCenters") {
        return { name: t("settings.alignHCenters"), shortcut: "" };
    } else if (codeName === "alignRight") {
        return { name: t("settings.alignRight"), shortcut: "" };
    } else if (codeName === "alignTop") {
        return { name: t("settings.alignTop"), shortcut: "" };
    } else if (codeName === "alignVCenters") {
        return { name: t("settings.alignVCenters"), shortcut: "" };
    } else if (codeName === "alignBottom") {
        return { name: t("settings.alignBottom"), shortcut: "" };
    } else if (codeName === "navTrackpad") {
        return { name: t("settings.navTrackpad"), shortcut: "" };
    } else if (codeName === "navMouse") {
        return { name: t("settings.navMouse"), shortcut: "" };
    } else if (codeName === "setLanguageRu") {
        return { name: t("settings.languageRu"), shortcut: "" };
    } else if (codeName === "setLanguageEn") {
        return { name: t("settings.languageEn"), shortcut: "" };
    }

    return { name: "", shortcut: "" };
}
